// Image URL management for AWS S3/CloudFront
// Set VITE_IMAGE_BASE_URL or VITE_CLOUDFRONT_DOMAIN in .env to match your S3/CloudFront setup.
// If images still don't load, check S3 bucket policy (public read) and CORS (see README or docs).

const getImageBaseUrl = (): string => {
  const base = import.meta.env.VITE_IMAGE_BASE_URL;
  if (base && typeof base === 'string') {
    return base.replace(/\/$/, ''); // strip trailing slash
  }
  const domain = import.meta.env.VITE_CLOUDFRONT_DOMAIN || 'dhfbvjfrbuwbt.cloudfront.net';
  return `https://${domain}/Videos`;
};

const IMAGE_BASE = getImageBaseUrl();

// Optional explicit mapping for special cases (similar to videoUrls.ts pattern)
export const IMAGE_URLS: { [key: string]: string } = {
  '/_images/PolestarEngineered/PolestarEngineered-Hero.jpg': `${IMAGE_BASE}/_images/PolestarEngineered/PolestarEngineered-Hero.jpg`,
};

/**
 * Transforms a local image path to an S3/CloudFront URL (or local path if VITE_USE_LOCAL_IMAGES=true).
 * @param imagePath - The local image path (e.g., '/projects/Formula1/F1-1.jpg')
 * @returns The S3/CloudFront URL or local path
 */
export const getImageUrl = (imagePath: string): string => {
  if (!imagePath || imagePath.trim() === '') {
    console.warn('getImageUrl: Empty or invalid image path provided');
    return '';
  }

  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // Serve from local public folder (e.g. when S3 is unavailable or for offline dev)
  if (import.meta.env.VITE_USE_LOCAL_IMAGES === 'true') {
    const local = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return local;
  }

  if (IMAGE_URLS[imagePath]) {
    return IMAGE_URLS[imagePath];
  }

  let cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;

  if (cleanPath.startsWith('_images/')) {
    const finalUrl = `${IMAGE_BASE}/${cleanPath}`;
    if (import.meta.env.DEV) {
      console.log(`getImageUrl: ${imagePath} -> ${finalUrl}`);
    }
    return finalUrl;
  }

  if (cleanPath.startsWith('projects/')) {
    cleanPath = cleanPath.slice('projects/'.length);
  }

  const finalUrl = `${IMAGE_BASE}/_images/${cleanPath}`;
  if (import.meta.env.DEV) {
    console.log(`getImageUrl: ${imagePath} -> ${finalUrl}`);
  }
  return finalUrl;
};

/**
 * Helper function to check if image is hosted on S3/CloudFront (remote URL).
 * @param imagePath - The image path to check
 * @returns True if the image resolves to a remote URL (S3/CloudFront)
 */
export const isS3Image = (imagePath: string): boolean => {
  const url = getImageUrl(imagePath);
  return url.startsWith('http://') || url.startsWith('https://');
};

/**
 * Batch transform an array of image paths to S3 URLs
 * @param imagePaths - Array of local image paths
 * @returns Array of S3/CloudFront URLs
 */
export const getImageUrls = (imagePaths: string[]): string[] => {
  return imagePaths.map(path => getImageUrl(path));
};

